import { useQuery } from 'react-query';

import {
  ActivityMatterSortKey,
  ActivityMattersResponseDto,
  ActivityOverviewResponseDto,
  ActivityUsersResponseDto,
} from '@/../../backend/src/usage/dto/activity-overview.response.dto';
import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import { useFetcher } from '@/api/useGetFetchConfig';

type ActivityFilters = { startDate?: string; endDate?: string };

function activityParams(organisationId: string, filters: ActivityFilters) {
  const params = new URLSearchParams({ organisationId });
  if (filters.startDate) params.set('startDate', filters.startDate);
  if (filters.endDate) params.set('endDate', filters.endDate);
  return params;
}

export function useGetActivityOverview(organisationId: string, filters: ActivityFilters = {}) {
  const fetcher = useFetcher();
  const params = activityParams(organisationId, filters);
  return useQuery<ActivityOverviewResponseDto>(
    [QueryEntity.ActivityOverview, { organisationId, ...filters }],
    () => fetcher(`${APIBaseChronos}/api/usage/activity-overview?${params}`),
    { enabled: organisationId !== '', keepPreviousData: true, refetchOnWindowFocus: false },
  );
}

export function useGetActivityMatters(
  organisationId: string,
  page: number,
  pageSize = 15,
  filters: ActivityFilters = {},
  sort: { sortBy: ActivityMatterSortKey; sortOrder: 'ASC' | 'DESC' } = { sortBy: 'documents', sortOrder: 'DESC' },
  enabled = true,
) {
  const fetcher = useFetcher();
  const params = activityParams(organisationId, filters);
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  params.set('sortBy', sort.sortBy);
  params.set('sortOrder', sort.sortOrder);
  return useQuery<ActivityMattersResponseDto>(
    [QueryEntity.ActivityOverview, 'matters', { organisationId, page, pageSize, ...filters, ...sort }],
    () => fetcher(`${APIBaseChronos}/api/usage/activity-matters?${params}`),
    { enabled: enabled && organisationId !== '', keepPreviousData: true, refetchOnWindowFocus: false },
  );
}

export function useGetActivityUsers(organisationId: string, filters: ActivityFilters = {}, enabled = true) {
  const fetcher = useFetcher();
  const params = activityParams(organisationId, filters);
  return useQuery<ActivityUsersResponseDto>(
    [QueryEntity.ActivityOverview, 'users', { organisationId, ...filters }],
    () => fetcher(`${APIBaseChronos}/api/usage/activity-users?${params}`),
    { enabled: enabled && organisationId !== '', keepPreviousData: true, refetchOnWindowFocus: false },
  );
}
